import { createContext } from 'react'
import type { FocusEvent, KeyboardEvent } from 'react'

/** Wiring the combobox root hands to the actions pinned inside its popup. MUI renders that popup as a
 * sibling of the root rather than a child, so the two cannot reach each other through the DOM. Null
 * for any autocomplete whose paper pins no actions. */
export type AutocompleteActionsFocus = {
  registerRegion: (region: HTMLElement | null) => void
  onRegionKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  onRegionBlur: (event: FocusEvent<HTMLElement>) => void
}

export const AutocompleteActionsContext = createContext<AutocompleteActionsFocus | null>(null)

const TABBABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]'

// A disabled action cannot take focus, and Tab's default is already suppressed by the time it is chosen.
export const actionButtonsIn = (region: HTMLElement | null): HTMLElement[] =>
  region === null ? [] : Array.from(region.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]'))

/** MUI closes the popup when the input blurs, and focus sitting on an action has already been past
 * that, so route the close back through the input rather than reaching for the open state — which an
 * uncontrolled Autocomplete keeps to itself. Focus is restored synchronously, before any paint. */
export const closeByBlurringInput = (root: HTMLElement | null, restoreFocusTo: HTMLElement | null) => {
  const input = root?.querySelector('input') ?? null
  input?.focus({ preventScroll: true })
  input?.blur()
  restoreFocusTo?.focus({ preventScroll: true })
}

export const nextTabbableAfter = (root: HTMLElement): HTMLElement | null =>
  Array.from(document.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).find(
    (candidate) =>
      !root.contains(candidate) &&
      // Popups are portalled to the end of the body, so they follow every root in document order.
      candidate.closest('.MuiAutocomplete-popper') === null &&
      Boolean(root.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) &&
      candidate.tabIndex >= 0 &&
      !candidate.matches(':disabled') &&
      candidate.getClientRects().length > 0
  ) ?? null
